import React, { useState, useEffect } from 'react';
import { 
  Ticket, X, Gift, Trophy, Calendar, Mail, Sparkles, 
  ChevronRight, Star, Award, Clock
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RaffleTicketPortal = ({ isOpen, onClose, user }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, thisMonth: 0 });
  
  const token = localStorage.getItem('auth_token');

  // Fetch user's raffle tickets
  useEffect(() => {
    if (isOpen && user && token) {
      fetchTickets();
    }
  }, [isOpen, user]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/raffle/my-tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTickets(data.tickets || []);
        setStats({
          total: data.total || 0,
          thisMonth: data.this_month || 0
        });
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal - full width on mobile, max-w-2xl on desktop */}
      <Card className="relative z-10 w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 p-4 sm:p-6 text-white relative overflow-hidden">
          {/* Animated sparkles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <Sparkles 
                key={i}
                className="absolute text-white/20 animate-pulse"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  width: `${12 + Math.random() * 12}px`
                }}
              />
            ))}
          </div>
          
          <div className="relative">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Ticket className="w-5 h-5 sm:w-8 sm:h-8" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-2xl font-bold truncate">RAFFLE TICKETS</h2>
                  <p className="text-white/90 text-xs sm:text-sm truncate">Powered by ASAP-247/365!LOGISTICS</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-white hover:bg-white/20 flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Prize Section - scrollable content area */}
        <div className="p-3 sm:p-6 bg-gradient-to-b from-gray-50 to-white overflow-y-auto" style={{ maxHeight: 'calc(95vh - 180px)' }}>
          <div className="text-center mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">
              <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              Monthly Prize Draw
            </h3>
            <div className="grid grid-cols-3 gap-2 sm:gap-3 max-w-md mx-auto">
              <div className="bg-gradient-to-b from-yellow-400 to-yellow-500 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center shadow-lg">
                <Award className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-900 mx-auto mb-0.5 sm:mb-1" />
                <p className="text-[10px] sm:text-xs font-bold text-yellow-900">1ST PRIZE</p>
                <p className="text-base sm:text-xl font-black text-yellow-900">$150</p>
              </div>
              <div className="bg-gradient-to-b from-gray-300 to-gray-400 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center shadow-lg">
                <Award className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 mx-auto mb-0.5 sm:mb-1" />
                <p className="text-[10px] sm:text-xs font-bold text-gray-700">2ND PRIZE</p>
                <p className="text-base sm:text-xl font-black text-gray-700">$100</p>
              </div>
              <div className="bg-gradient-to-b from-amber-600 to-amber-700 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center shadow-lg">
                <Award className="w-4 h-4 sm:w-5 sm:h-5 text-amber-200 mx-auto mb-0.5 sm:mb-1" />
                <p className="text-[10px] sm:text-xs font-bold text-amber-200">3RD PRIZE</p>
                <p className="text-base sm:text-xl font-black text-amber-200">$50</p>
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 border border-blue-200">
            <h4 className="font-bold text-blue-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
              How It Works
            </h4>
            <ul className="space-y-2 text-sm text-blue-700">
              <li className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span className="text-xs sm:text-sm">Every delivery by <strong>ASAP-247/365!LOGISTICS</strong> earns <strong>1 ticket</strong></span>
              </li>
              <li className="flex items-start gap-2 text-xs sm:text-sm">
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0" />
                <span>Tickets added automatically when delivery is completed</span>
              </li>
              <li className="flex items-start gap-2 text-xs sm:text-sm">
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0" />
                <span>Monthly draw on the <strong>last day of each month</strong></span>
              </li>
              <li className="flex items-start gap-2 text-xs sm:text-sm">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0" />
                <span><strong>Winners announced via email</strong></span>
              </li>
            </ul>
          </div>

          {/* User Stats */}
          {user ? (
            <div className="mb-4 sm:mb-6">
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl p-3 sm:p-4 text-white text-center">
                  <Ticket className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1 sm:mb-2 opacity-80" />
                  <p className="text-2xl sm:text-3xl font-black">{stats.total}</p>
                  <p className="text-[10px] sm:text-xs text-purple-200">Total Tickets</p>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl p-3 sm:p-4 text-white text-center">
                  <Calendar className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1 sm:mb-2 opacity-80" />
                  <p className="text-2xl sm:text-3xl font-black">{stats.thisMonth}</p>
                  <p className="text-[10px] sm:text-xs text-green-200">This Month</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 rounded-lg sm:rounded-xl border border-amber-200 text-center">
              <p className="text-amber-700 font-medium text-sm">Sign in to view your raffle tickets!</p>
            </div>
          )}

          {/* Tickets List */}
          {user && (
            <div>
              <h4 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                Your Tickets
              </h4>
              
              {loading ? (
                <div className="text-center py-6 sm:py-8">
                  <div className="animate-spin w-6 h-6 sm:w-8 sm:h-8 border-4 border-red-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-gray-500 text-sm">Loading tickets...</p>
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-6 sm:py-8 bg-gray-50 rounded-lg sm:rounded-xl">
                  <Ticket className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 font-medium text-sm">No tickets yet</p>
                  <p className="text-xs sm:text-sm text-gray-400">Order with delivery to earn your first ticket!</p>
                </div>
              ) : (
                <div className="max-h-36 sm:max-h-48 overflow-y-auto space-y-2">
                  {tickets.map((ticket, idx) => (
                    <div 
                      key={ticket.id || idx}
                      className="flex items-center justify-between p-2 sm:p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-100"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                          <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-mono font-bold text-gray-800 text-xs sm:text-sm truncate">{ticket.ticket_number}</p>
                          <p className="text-[10px] sm:text-xs text-gray-500 truncate">Order #{ticket.order_number}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <Badge className="bg-green-100 text-green-700 text-[10px] sm:text-xs">Active</Badge>
                        <p className="text-[10px] sm:text-xs text-gray-400 mt-1">{formatDate(ticket.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Next Draw Info */}
          <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg sm:rounded-xl text-white text-center">
            <Clock className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2" />
            <p className="font-bold text-sm sm:text-base">Next Draw</p>
            <p className="text-xs sm:text-sm opacity-90">End of this month</p>
            <p className="text-[10px] sm:text-xs opacity-75 mt-1">Winners notified by email</p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 bg-gray-100 border-t">
          <Button 
            className="w-full bg-red-500 hover:bg-red-600 text-white text-sm sm:text-base"
            onClick={onClose}
          >
            Got it! Order more to get tickets
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default RaffleTicketPortal;
